import React, { useState, useMemo } from 'react';
import { X, Layout, LayoutGrid, MessageSquare, MousePointer, Footprints, BarChart3, DollarSign, Layers } from 'lucide-react';
import TemplateCard from './TemplateCard';
import { templateCategories, allTemplates, getCategoryNames } from '../../../templates/sections';

/**
 * Category configuration with icons
 */
const CATEGORY_CONFIG = {
  all: { label: 'All', icon: Layers },
  hero: { label: 'Hero', icon: Layout },
  content: { label: 'Content', icon: BarChart3 },
  features: { label: 'Features', icon: LayoutGrid },
  testimonials: { label: 'Testimonials', icon: MessageSquare },
  cta: { label: 'CTA', icon: MousePointer },
  pricing: { label: 'Pricing', icon: DollarSign },
  footer: { label: 'Footer', icon: Footprints },
};

/**
 * TemplateLibraryModal - Modal for browsing and selecting templates
 *
 * Features:
 * - Category tabs for filtering
 * - Grid of template cards
 * - Template selection and insertion
 */
const TemplateLibraryModal = ({ isOpen, onClose, onSelectTemplate }) => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Get filtered templates based on active category
  const filteredTemplates = useMemo(() => {
    if (activeCategory === 'all') {
      return allTemplates;
    }
    return templateCategories[activeCategory] || [];
  }, [activeCategory]);

  // Get available categories (only show categories that have templates, plus 'all')
  const availableCategories = useMemo(() => {
    const categoryNames = getCategoryNames();
    return ['all', ...categoryNames.filter(cat =>
      templateCategories[cat] && templateCategories[cat].length > 0
    )];
  }, []);

  if (!isOpen) {
    return null;
  }

  const handleInsert = () => {
    if (selectedTemplate) {
      onSelectTemplate(selectedTemplate);
      onClose();
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[80vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Template Library</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Category Tabs */}
        <div className="flex border-b border-gray-200 px-6 bg-gray-50">
          {availableCategories.map((category) => {
            const config = CATEGORY_CONFIG[category] || { label: category, icon: Layout };
            const Icon = config.icon;
            const isActive = activeCategory === category;

            return (
              <button
                key={category}
                onClick={() => {
                  setActiveCategory(category);
                  setSelectedTemplate(null);
                }}
                className={`
                  flex items-center gap-2 px-4 py-3 text-sm font-medium
                  transition-colors border-b-2 -mb-px
                  ${isActive
                    ? 'text-blue-600 border-blue-600 bg-white'
                    : 'text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-100'
                  }
                `}
              >
                <Icon className="w-4 h-4" />
                <span>{config.label}</span>
              </button>
            );
          })}
        </div>

        {/* Template Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          {filteredTemplates.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredTemplates.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  isSelected={selectedTemplate?.id === template.id}
                  onSelect={setSelectedTemplate}
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-gray-500">
              <Layout className="w-12 h-12 mb-4 text-gray-300" />
              <p className="text-sm">No templates available in this category</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleInsert}
            disabled={!selectedTemplate}
            className={`
              px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors
              ${selectedTemplate
                ? 'bg-blue-600 hover:bg-blue-700'
                : 'bg-gray-300 cursor-not-allowed'
              }
            `}
          >
            Insert Template
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateLibraryModal;
